/*
 * /classes/:slug/onboarding — "Làm quen với Simba": 9 chặng, mỗi chặng vài task nhỏ, xong chặng mở badge.
 * API: GET /api/classes/:slug/onboarding → { progress, items[{ id, sort_order, title, description, badge_name,
 *        completed, completed_tasks, tasks[{ id, sort_order, title, instruction, completed }] }], next_item_id }
 *      POST { event_id, task_id } → cùng hình dạng + badge_awarded?
 */
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Icon, SemanticIcon } from "@/components/icon"
import { ErrorState, LoadingState, Meter, Page, PageHeader, StatusNote, usePageTitle } from "@/components/page"
import { BadgeMedallion } from "@/components/simba"
import { useClass } from "@/app/ClassLayout"
import { api, post } from "@/lib/api"
import { cn } from "@/lib/utils"
import { Disclosure } from "./community/shared"

function TaskRow({ task, busy, onDone }: { task: any; busy: string; onDone: (id: string) => void }) {
  return (
    <li className={cn("flex flex-col gap-3 rounded-2xl border-[1.5px] p-4 sm:flex-row sm:items-center", task.completed ? "border-transparent bg-success-soft/60" : "border-edge bg-card")}>
      <span className={cn("grid size-9 shrink-0 place-items-center rounded-full font-heading font-bold", task.completed ? "bg-success text-white" : "bg-soft text-orange-strong")}>
        {task.completed ? <Icon name="check" className="size-4" /> : task.sort_order}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink">{task.title}</p>
        <p className="text-sm text-muted-foreground">{task.instruction}</p>
      </div>
      <Button size="sm" variant={task.completed ? "secondary" : "default"} disabled={task.completed || !!busy} onClick={() => onDone(task.id)} className="self-start sm:self-center">
        {task.completed ? "Đã xong" : busy === task.id ? "Đang lưu…" : "Xong"}
      </Button>
    </li>
  )
}

export default function OnboardingPage() {
  usePageTitle("Làm quen với Simba", "Hành trình làm quen từng bước nhỏ.")
  const { slug } = useClass()
  const [data, setData] = useState<any>()
  const [activeId, setActiveId] = useState("")
  const [busy, setBusy] = useState("")
  const [error, setError] = useState("")
  const [note, setNote] = useState<{ tone: "success" | "error"; text: string } | null>(null)
  const path = `/api/classes/${encodeURIComponent(slug)}/onboarding`

  const load = () => {
    setError("")
    api(path)
      .then((next: any) => {
        setData(next)
        setActiveId((cur) => cur || next.next_item_id || next.items[0]?.id || "")
      })
      .catch((e: any) => setError(e.message))
  }
  useEffect(() => { load() }, [slug])

  if (error) return <Page><ErrorState message={error} onRetry={load} /></Page>
  if (!data) return <LoadingState label="Simba đang chuẩn bị hành trình…" />
  const items: any[] = data.items || []
  const active = items.find((i) => i.id === activeId) || items[0]
  const p = data.progress || {}

  const completeTask = async (taskId: string) => {
    setBusy(taskId)
    setNote(null)
    try {
      const next: any = await post(path, { event_id: crypto.randomUUID(), task_id: taskId })
      setData(next)
      setNote({ tone: "success", text: next.badge_awarded ? `Con đã nhận badge “${next.badge_awarded}”!` : "Simba đã ghi nhận task này vào hành trình của con." })
    } catch (e: any) {
      setNote({ tone: "error", text: e.message })
    } finally {
      setBusy("")
    }
  }
  const select = (id: string) => {
    setActiveId(id)
    setNote(null)
    scrollTo({ top: 0, behavior: "smooth" })
  }
  const nextItem = items.find((i) => !i.completed)

  return (
    <Page className="max-w-[820px]">
      <PageHeader eyebrow="Onboarding" title="Làm quen với Simba" description="Mỗi lần chỉ cần làm một việc nhỏ." />

      <Card size="sm">
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <strong className="font-heading text-3xl text-orange-strong">{p.percentage ?? 0}%</strong>
            <span className="text-sm text-muted-foreground">{p.completed_tasks}/{p.total_tasks} task</span>
            <span className="ml-auto text-sm font-semibold text-ink">{p.completed_items}/{items.length} badge đã mở</span>
          </div>
          <Meter value={p.percentage ?? 0} />
        </CardContent>
      </Card>

      {active && (
        <Card className={cn(active.completed && "border-success/40")}>
          <CardContent className="flex flex-col gap-5">
            <div className="flex items-start gap-4">
              <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-soft">
                <SemanticIcon label={active.title} className="size-7" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Chặng {active.sort_order}/{items.length}</p>
                <h2 className="font-heading text-xl font-semibold text-ink">{active.title}</h2>
                <p className="text-sm text-muted-foreground">{active.description}</p>
              </div>
              {active.completed && <Icon name="check-circle" className="size-7 text-success" />}
            </div>
            <ol className="flex flex-col gap-3">
              {active.tasks.map((t: any) => <TaskRow key={t.id} task={t} busy={busy} onDone={completeTask} />)}
            </ol>
            {active.completed && (
              <div className="flex flex-col items-center gap-3 rounded-2xl bg-sand p-4 text-center sm:flex-row sm:text-left">
                <BadgeMedallion name={active.badge_name || active.title} earned className="w-16" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Badge</p>
                  <p className="font-heading text-lg font-semibold text-ink">{active.badge_name}</p>
                </div>
                {nextItem && (
                  <Button onClick={() => select(nextItem.id)}>
                    Chặng tiếp <Icon name="arrow-right" />
                  </Button>
                )}
              </div>
            )}
            {note && <StatusNote tone={note.tone}>{note.text}</StatusNote>}
          </CardContent>
        </Card>
      )}

      <Disclosure title={`Xem ${items.length} chặng`}>
        <ul className="grid gap-2 sm:grid-cols-2">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => select(item.id)}
                aria-current={item.id === active?.id ? "step" : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-2xl border-[1.5px] p-3 text-left transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
                  item.id === active?.id ? "border-orange bg-soft" : "border-edge bg-card hover:border-orange",
                )}
              >
                <span className={cn("grid size-8 shrink-0 place-items-center rounded-full text-sm font-bold", item.completed ? "bg-success text-white" : "bg-soft text-orange-strong")}>
                  {item.completed ? <Icon name="check" className="size-4" /> : item.sort_order}
                </span>
                <span className="min-w-0 flex-1 truncate font-semibold text-ink">{item.title}</span>
                <span className="text-sm text-muted-foreground">{item.completed_tasks}/{item.tasks.length}</span>
              </button>
            </li>
          ))}
        </ul>
      </Disclosure>
    </Page>
  )
}
